import React from 'react';
import { Box, CircularProgress, Typography, Alert } from '@mui/material';
import { useAuth } from '../../hooks/useAuth';

interface AuthenticationGuardProps {
  children: React.ReactNode;
}

/**
 * Mandatory authentication guard for the application
 * When auth is enabled, all users must authenticate before accessing the app
 * When auth is disabled, users can access the app normally
 */
export const AuthenticationGuard: React.FC<AuthenticationGuardProps> = ({ children }) => {
  const { isAuthenticated, isLoading, error, loginWithRedirect } = useAuth();
  const isAuthEnabled = import.meta.env.VITE_AUTH_ENABLED === 'true';

  // If auth is disabled, allow normal app access
  if (!isAuthEnabled) {
    return <>{children}</>;
  }

  // Show loading while determining authentication status
  if (isLoading) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
      >
        <CircularProgress size={60} />
        <Typography variant="h6">Verifying session...</Typography>
      </Box>
    );
  }

  // Show error if authentication failed
  if (error) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
        minHeight="100vh"
        gap={2}
        p={3}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          Authentication error: {error.message}
        </Alert>
        <Typography variant="body2" color="text.secondary">
          Please refresh the page to try again.
        </Typography>
      </Box>
    );
  }

  // If user is authenticated, show the app
  if (isAuthenticated) {
    return <>{children}</>;
  }

  // If user is not authenticated and auth is enabled, redirect to Auth0
  // This should happen automatically, but we also handle manual trigger
  React.useEffect(() => {
    if (!isAuthenticated && !isLoading && !error) {
      loginWithRedirect({
        appState: {
          returnTo: window.location.pathname
        }
      });
    }
  }, [isAuthenticated, isLoading, error, loginWithRedirect]);

  // Show loading state while redirecting to Auth0
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight="100vh"
      gap={2}
    >
      <CircularProgress size={60} />
      <Typography variant="h6">Redirecting to sign in...</Typography>
    </Box>
  );
};